import React, { useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import { useCesium } from '../../../../context/CesiumContext';
import { getReportUsedEntities } from '../../../../lib/accessAnalysisReports';
import { deleteGeoFeature, deleteGeoFeatures } from '../../../../service/geoFeatureApi';
import { TreeSection, GeoFeatureIcon, TargetTrackIcon, type SortOption } from '../../Common';
import TreeItem, { TreeItemContext } from '../../Satellite/components/TreeItem';
import type { GeoFeature } from '../../../../types/mission';
import type { MissionStore } from '../../../../../../../store/MissionSlice';
import { showToastMessage } from '../../../../../../../utils/commonFunctions';
import styles from '../../LeftSideBar.module.scss';
import ConfirmationDialog from '../../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog';

interface GeoFeatureSectionProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  disabled?: boolean;
}

const toggleSelectedId = (ids: string[], id: string) => (
  ids.includes(id) ? ids.filter(existing => existing !== id) : [...ids, id]
);

function sortItems(items: GeoFeature[], sort: SortOption): GeoFeature[] {
  return [...items].sort((a, b) => {
    if (sort === 'az') return a.name.localeCompare(b.name);
    if (sort === 'za') return b.name.localeCompare(a.name);
    
    const aT = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bT = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    
    if (aT !== bT) {
      return sort === 'newest' ? bT - aT : aT - bT;
    }
    
    const aIdTime = parseInt(a.id.replace(/\D/g, '').slice(0, 13), 10) || 0;
    const bIdTime = parseInt(b.id.replace(/\D/g, '').slice(0, 13), 10) || 0;
    return sort === 'newest' ? bIdTime - aIdTime : aIdTime - bIdTime;
  });
}

const GeoFeatureSection: React.FC<GeoFeatureSectionProps> = ({ collapsed, onToggleCollapsed, disabled = false }) => {
  const {
    geoFeatures, selectedItemId, selectedGeoFeatureIds, openGeoFeature, selectTreeItem, toggleSelectedGeoFeature, toggleGeoFeatureActive,
    removeGeoFeature, updateGeoFeature, openEditGeoFeature, analyserOpen, coverageAnalyserOpen, reportHoverHighlights,
    shouldBlockUI, selectedReportDetail, selectedCoverageReportDetail, satellites, invalidateAccessWindows, geoFeaturePanelOpen,
  } = useMDCockpit();
  const userData = useSelector((state: { mission: MissionStore }) => state.mission.userData);
  const cesium = useCesium();
  const { mission_id = '', scenario_id = '' } = useParams<{ mission_id: string; scenario_id: string }>();

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortOption>('newest');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deletingGeo, setDeletingGeo] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [deletingPending, setDeletingPending] = useState(false);
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);

  React.useEffect(() => {
    if (analyserOpen || coverageAnalyserOpen) {
      setSelectedIds([]);
    }
  }, [analyserOpen, coverageAnalyserOpen]);

  const handleAddGeoFeature = useCallback(() => {
    const maxGeofence = userData?.['feature-allowed']?.['missionModelMaxGeofeature'] || 0;
    const currentGeoCount = geoFeatures.length;

    if (maxGeofence > 0 && currentGeoCount >= maxGeofence) {
      showToastMessage('error', `You have reached the maximum limit of ${maxGeofence} geo features. Please delete an existing geo feature to add a new one.`);
      return;
    }

    openGeoFeature();
  }, [userData, geoFeatures.length, openGeoFeature]);

  const hoverGeoFeatureIds = useMemo(() => new Set(reportHoverHighlights.geoFeatureIds), [reportHoverHighlights.geoFeatureIds]);
  const activeReport = selectedReportDetail || selectedCoverageReportDetail;
  const usedEntities = useMemo(() => getReportUsedEntities(activeReport, satellites), [activeReport, satellites]);
  const isReportActive = activeReport !== null;

  const filteredGeo = useMemo(() => {
    const q = query.toLowerCase();
    const list = q ? geoFeatures.filter(g => g.name.toLowerCase().includes(q)) : geoFeatures;
    return sortItems(list, sort);
  }, [geoFeatures, query, sort]);

  const deleteGeoFeatureById = useCallback(async (id: string) => {
    try {
      const res: any = await deleteGeoFeature(mission_id, scenario_id, id);
      removeGeoFeature(id);
      cesium.removeGeoFeature(id);
      invalidateAccessWindows();
      if (selectedItemId === id) selectTreeItem(null, null);
      
      const successMsg = res?.data?.message || res?.message || 'GeoFeature deleted successfully';
      showToastMessage('success', successMsg);
    } catch (e: any) {
      console.error('[GeoFeature] Delete failed', e);
      const errorMsg = e?.response?.data?.message || e?.response?.data?.Message || e?.data?.message || e?.message || 'Failed to delete GeoFeature';
      showToastMessage('error', errorMsg);
      throw e;
    }
  }, [mission_id, scenario_id, removeGeoFeature, selectedItemId, selectTreeItem, invalidateAccessWindows, cesium]);

  const confirmPendingDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeletingPending(true);
    try {
      await deleteGeoFeatureById(pendingDelete.id);
    } catch (e) {
      console.error('[GeoFeature] Delete failed in confirmation', e);
    } finally {
      setPendingDelete(null);
      setDeletingPending(false);
    }
  }, [pendingDelete, deleteGeoFeatureById]);

  const handleDeleteSelectedGeo = useCallback(async () => {
    if (selectedIds.length === 0) return;
    setDeletingGeo(true);
    try {
      const res: any = await deleteGeoFeatures(mission_id, scenario_id, selectedIds);
      selectedIds.forEach(id => {
        removeGeoFeature(id);
        cesium.removeGeoFeature(id);
      });
      invalidateAccessWindows();
      setSelectedIds([]);
      if (selectedItemId && selectedIds.includes(selectedItemId)) selectTreeItem(null, null);
      
      const successMsg = res?.data?.message || res?.message || 'GeoFeatures deleted successfully';
      showToastMessage('success', successMsg);
    } catch (e: any) {
      console.error('[GeoFeature] Bulk delete failed', e);
      const errorMsg = e?.response?.data?.message || e?.response?.data?.Message || e?.data?.message || e?.message || 'Failed to delete selected GeoFeatures';
      showToastMessage('error', errorMsg);
    } finally {
      setConfirmingBulkDelete(false);
      setDeletingGeo(false);
    }
  }, [selectedIds, removeGeoFeature, selectedItemId, selectTreeItem, mission_id, scenario_id, invalidateAccessWindows, cesium]);

  return (
    <>
      <TreeItemContext.Provider value={{ openMenuId, setOpenMenuId }}>
        <TreeSection
          title="GeoFeatures"
          count={geoFeatures.length}
          onAdd={handleAddGeoFeature}
          query={query}
          onQuery={setQuery}
          sort={sort}
          onSort={setSort}
          empty={filteredGeo.length === 0}
          emptyText="No features yet"
          collapsed={collapsed}
          onToggleCollapsed={onToggleCollapsed}
          disabled={disabled || shouldBlockUI}
          modalOpen={geoFeaturePanelOpen}
        >
          {filteredGeo.map((gf: GeoFeature) => (
            <TreeItem
              key={gf.id}
              id={gf.id}
              name={gf.name}
              dot="none"
              active={gf.active}
              icon={gf.kind === 'point' ? <TargetTrackIcon /> : <GeoFeatureIcon />}
              selected={selectedItemId === gf.id || selectedGeoFeatureIds.includes(gf.id) || selectedIds.includes(gf.id) || hoverGeoFeatureIds.has(gf.id)}
              showDot={false}
              onSelect={() => {
                toggleSelectedGeoFeature(gf.id);
              }}
              onToggleActive={() => { toggleGeoFeatureActive(gf.id); cesium.setGeoFeatureVisible(gf.id, !gf.active); }}
              onEdit={() => { cesium.setClockPlaying(false); openEditGeoFeature(gf.id); }}
              onDelete={() => deleteGeoFeatureById(gf.id)}
              onRequestDelete={() => setPendingDelete({ id: gf.id, name: gf.name })}
              onHover={(id) => { cesium.highlightGeoFeature(id); }}
              disabled={shouldBlockUI}
              unusedInReport={isReportActive && !usedEntities.geoFeatureIds.has(gf.id)}
            />
          ))}
        </TreeSection>
      </TreeItemContext.Provider>

      {ReactDOM.createPortal(
        <>
          <ConfirmationDialog
            type="delete"
            open={pendingDelete !== null}
            title="Delete GeoFeature?"
            loading={deletingPending}
            description={
              pendingDelete
                ? `Deleting ${pendingDelete.name} will permanently remove it from the database, and it cannot be restored once deleted.`
                : ''
            }
            confirm_button_text="Delete"
            onConfirm={confirmPendingDelete}
            onCancel={() => setPendingDelete(null)}
          />

          <ConfirmationDialog
            type="delete"
            open={confirmingBulkDelete}
            title="Delete GeoFeatures?"
            loading={deletingGeo}
            description={`Deleting selected GeoFeatures will permanently remove them from the database, and they cannot be restored once deleted.`}
            confirm_button_text="Delete"
            onConfirm={handleDeleteSelectedGeo}
            onCancel={() => setConfirmingBulkDelete(false)}
          />
        </>,
        document.body
      )}

      <div className={styles.segmentDivider} />
    </>
  );
};

export default GeoFeatureSection;
